import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { authFetch } from '../../utils/api';

export function CourseCatalogue({ onClose }) {
  const { token } = useAuth();
  const [courses, setCourses] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [selectedSkill, setSelectedSkill] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [freeOnly, setFreeOnly] = useState(false);
  const [skills, setSkills] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const searchTimeout = useRef(null);

  useEffect(() => {
    authFetch('/api/train/catalogue?page=1&per_page=1', token)
      .then(d => { setSkills(d.skills || []); setProviders(d.providers || []); })
      .catch(() => {});
  }, [token]);

  const doSearch = useCallback((q, sk, pv, fr, pg) => {
    setLoading(true);
    const params = new URLSearchParams({ page: pg, per_page: 30 });
    if (q) params.set('q', q);
    if (sk) params.set('skill', sk);
    if (pv) params.set('provider', pv);
    if (fr) params.set('free', 'true');
    authFetch(`/api/train/catalogue?${params}`, token)
      .then(d => { setCourses(d.courses || []); setTotal(d.total || 0); setPages(d.pages || 1); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => doSearch(search, selectedSkill, selectedProvider, freeOnly, page), 300);
  }, [search, selectedSkill, selectedProvider, freeOnly, page, doSearch]);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-card)', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', padding: '16px 16px 48px' }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--border)', margin: '0 auto 12px' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{'\u{1F4DA}'} Course Catalogue</div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>{total} courses</div>
        </div>
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search courses..."
          style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-body)', color: 'var(--text-primary)', fontSize: 15, marginBottom: 8, boxSizing: 'border-box' }} />
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          <select value={selectedSkill} onChange={e => { setSelectedSkill(e.target.value); setPage(1); }}
            style={{ flex: 1, minWidth: 100, padding: '6px 8px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-body)', color: 'var(--text-primary)', fontSize: 14 }}>
            <option value="">All skills</option>
            {skills.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={selectedProvider} onChange={e => { setSelectedProvider(e.target.value); setPage(1); }}
            style={{ flex: 1, minWidth: 100, padding: '6px 8px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-body)', color: 'var(--text-primary)', fontSize: 14 }}>
            <option value="">All providers</option>
            {providers.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <button onClick={() => { setFreeOnly(!freeOnly); setPage(1); }}
            style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${freeOnly ? 'var(--accent)' : 'var(--border)'}`, background: freeOnly ? 'rgba(59,130,246,0.12)' : 'var(--bg-body)', color: freeOnly ? 'var(--accent)' : 'var(--text-muted)', fontSize: 14, cursor: 'pointer' }}>
            Free only
          </button>
        </div>
        {loading ? <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>Loading...</div> : <>
          {courses.map(c => (
            <div key={c.id} style={{ background: 'var(--bg-body)', borderRadius: 10, padding: '12px 14px', marginBottom: 8, border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 3 }}>{c.title}</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>{c.provider}{c.skill ? ` \u00b7 ${c.skill}` : ''}</div>
              <a href={c.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 14, color: 'var(--accent)', textDecoration: 'none' }}>Open course \u2192</a>
            </div>
          ))}
          {pages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12 }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-body)', cursor: 'pointer', opacity: page === 1 ? 0.4 : 1 }}>\u2190</button>
              <span style={{ fontSize: 14, color: 'var(--text-muted)', padding: '6px 4px' }}>{page} / {pages}</span>
              <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}
                style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-body)', cursor: 'pointer', opacity: page === pages ? 0.4 : 1 }}>\u2192</button>
            </div>
          )}
        </>}
      </div>
    </div>
  );
}

export default CourseCatalogue;
